import React from "react";
import { Box, Typography, makeStyles, Theme } from "@material-ui/core";
import { CheckCircle, Error as ErrorIcon, GetApp } from "@material-ui/icons";
import { SignalRMessage, SignalRStatusEnum } from "./types";

type Props = {
  message: SignalRMessage;
  onActionClick?: () => void;
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  divider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  downloadLink: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: theme.palette.primary.main,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  timestamp: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const ExportSignalRMessage = ({ message, onActionClick }: Props) => {
  const classes = useStyles();

  const getTitle = () => {
    switch (message.status) {
      case SignalRStatusEnum.Success:
        return "Export Ready";
      case SignalRStatusEnum.Error:
        return "Export Failed";
      case SignalRStatusEnum.Neutral:
        return "Export Status";
      default:
        return "Export";
    }
  };

  const getIcon = () => {
    switch (message.status) {
      case SignalRStatusEnum.Success:
        return <CheckCircle className={classes.successIcon} />;
      case SignalRStatusEnum.Error:
        return <ErrorIcon className={classes.errorIcon} />;
      default:
        return null;
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        {getIcon()}
        <Typography variant="body1" noWrap style={{ maxWidth: 250 }}>
          {getTitle()}
        </Typography>
      </Box>

      <Box className={`${classes.content} ${classes.divider}`}>
        {message.status === SignalRStatusEnum.Success && message.exportUrl ? (
          <>
            <Typography variant="body2">
              Export is ready to download.
            </Typography>
            <a
              className={classes.downloadLink}
              onClick={onActionClick}
              href={message.exportUrl}
              download
            >
              <GetApp fontSize="small" />
              <span>Download</span>
            </a>
          </>
        ) : (
          <>
            <Typography variant="body2">Something went wrong.</Typography>
            <Typography variant="body2">
              Try generating a report again.
            </Typography>
          </>
        )}

        <Typography className={classes.timestamp}>
          {new Date(message.timestamp).toLocaleTimeString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExportSignalRMessage;
